import { PSD_MIME_TYPE } from './asset-versions.js';

/** The file types an asset can serve as its image. */
const RENDERABLE_MIME_TYPES = ['image/png', 'image/webp', 'image/jpeg'];

/**
 * Where a PSD submission's background processing stands, as the API reports it:
 * the tile-brush layer extraction, or the flatten that renders a miniature's or
 * an image's preview. Null when nothing was queued for it.
 * @typedef {object} SubmissionProcessingStatus
 * @property {'processing'|'failed'|'ready'} status
 * @property {string} [error] why it failed
 */

/**
 * @typedef {object} PhaseSubmission
 * @property {string} [mime_type]
 * @property {Array<object>} [layers] extracted PSD layers (tile brushes) or the
 *   rendered preview (miniatures, image PSDs)
 * @property {SubmissionProcessingStatus|null} [processing]
 */

/**
 * The processing state of one submission, for the page to show under it.
 * @param {PhaseSubmission} submission
 * @returns {{state: 'processing'|'failed'|'ready'|null, message: string|null}}
 */
export function submissionProcessing(submission) {
    const processing = submission?.processing;
    if (!processing) return { state: null, message: null };
    if (processing.status === 'failed') {
        return {
            state: 'failed',
            message: `Couldn’t process the PSD: ${processing.error || 'unknown error'}`,
        };
    }
    if (processing.status === 'processing') {
        return { state: 'processing', message: 'Processing the PSD…' };
    }
    return { state: 'ready', message: null };
}

/**
 * Why a phase can't be submitted for review yet, or null when it can. Mirrors
 * the server's ValidatePhaseReadyForReview and the tile-brush extraction gate
 * SubmitForReview applies, and also waits for a PSD's preview so nothing is
 * sent for review that can't be shown. A disabled Submit button shows this, so
 * it is never greyed out without a reason.
 *
 * Miniatures and images are PSD-first: the server flattens each PSD into the
 * image the reviewer sees and the asset serves. An image may instead be a PNG,
 * WebP or JPEG, served as is.
 *
 * @param {string} assetType
 * @param {string} phaseType
 * @param {PhaseSubmission[]} submissions
 * @returns {string|null}
 */
export function phaseSubmitBlocker(assetType, phaseType, submissions) {
    const subs = submissions || [];
    if (subs.length === 0) return 'Upload your file first.';

    const failed = subs.map(submissionProcessing).find((p) => p.state === 'failed');
    if (failed) return failed.message;
    if (subs.some((s) => submissionProcessing(s).state === 'processing')) {
        return 'The PSD is still processing. This updates on its own when it’s done.';
    }

    if (assetType === 'miniature' && phaseType === 'delivery') {
        if (!subs.some((s) => s.mime_type === PSD_MIME_TYPE)) {
            return 'The miniature delivery needs its PSD.';
        }
    }
    if (assetType === 'image' && phaseType === 'delivery') {
        const servable = subs.some(
            (s) => s.mime_type === PSD_MIME_TYPE || RENDERABLE_MIME_TYPES.includes(s.mime_type),
        );
        if (!servable) return 'Upload the image as a PSD, PNG, WebP or JPEG.';
    }
    if (assetType === 'mapmaking-tile-brush') {
        // Exactly one PSD, and its layer extraction must have succeeded
        // server-side: no layers yet means extraction isn't done.
        if (subs.length !== 1) return 'A tile-brush phase takes exactly one PSD.';
        const sub = subs[0];
        if (sub.mime_type !== PSD_MIME_TYPE) return 'A tile-brush phase takes a PSD.';
        if ((sub.layers || []).length === 0) return 'The PSD hasn’t been processed yet.';
    }
    return null;
}

/**
 * Whether a phase's submissions are enough to submit it for review: when
 * phaseSubmitBlocker has no reason not to.
 * @param {string} assetType
 * @param {string} phaseType
 * @param {PhaseSubmission[]} submissions
 * @returns {boolean}
 */
export function phaseReadyForReview(assetType, phaseType, submissions) {
    return phaseSubmitBlocker(assetType, phaseType, submissions) === null;
}

/**
 * How many submissions a phase takes. Every miniature and tile-brush phase is
 * one PSD; other asset types take any number.
 * @param {string} assetType
 * @returns {number}
 */
export function maxSubmissionsForPhase(assetType) {
    if (assetType === 'miniature' || assetType === 'mapmaking-tile-brush') return 1;
    return Infinity;
}
